/**
 * useChecklistRecords Hook
 *
 * Manages checklist completion records with proper audit compliance.
 * Records are immutable - amendments via addendum only.
 *
 * FT-324 cycle 2 (2026-07-06): Migrated to `useStorageState` — the same
 * IDB-first read + LS fallback + async rehydrate pattern that useMainLogbook
 * uses reliably for cross-device sync. This closes two staging-empirical
 * bugs at once:
 *
 *   Race A — mount-race on Device B
 *     `useChecklistRecords` mounts lazily when the user clicks the
 *     Operations → Checklists tab (5–15s into boot). The sync bridge fires
 *     `sr:sync:hydrate` at t≈14s during the boot pull. The old hook attached
 *     its listener too late and missed the event; on next mount it read from
 *     LS (empty on Device B) and rendered an empty Recent list.
 *     `useStorageState` has an async IDB rehydrate on every mount, so it
 *     doesn't need to "catch" the hydrate event at all.
 *
 *   Race B — LS/IDB storage-backend split
 *     Old hook read/wrote via `lib/storage.js` (localStorage-only,
 *     synchronous). Sync bridge `handleCloudUpdate` writes via
 *     `storageRouter.writeJSON` which under the current feature flags
 *     (USE_INDEXEDDB=true, PARALLEL_STORAGE=false) lands ONLY in IndexedDB.
 *     Even when the listener attached in time, the pulled data was invisible
 *     because we were looking at the wrong backend.
 *     `useStorageState` reads IDB-first with LS fallback via storageRouter.
 *
 * FT-311 preservation:
 *   `useStorageState.setValue` writes to localStorage SYNCHRONOUSLY (step 1
 *   of setValue, line 501-506 in useStorageState.js) before queueing the
 *   async IDB write. Persistence lands before any React unmount can discard
 *   the state update — the exact FT-311 invariant. We ALSO use the function
 *   updater form `setValue(prev => [record, ...prev])` so rapid synchronous
 *   saves read from valueRef.current (freshly updated by prior setValue in
 *   the same tick) rather than from a stale `records` closure.
 *
 * Migration retention:
 *   The FT-324 W1 one-shot migration from the legacy alias key
 *   `seaready_checklist_records_${vesselId}` is preserved. It writes canonical
 *   FIRST via storage.js writeJSON, only removes the alias on write success.
 */

import { useCallback, useEffect } from 'react';
import { useVessel } from '../../../contexts/VesselContext';
import { readJSON, writeJSON, removeItem } from '../../../lib/storage';
import { useStorageState } from '../../useStorageState';
import {
  createCompletionRecord,
  validateCompletionRecord,
  CHECKLIST_TYPES
} from '../../../schemas/checklistCompletionSchema';

// FT-324: canonical key aligned with pull-side sync bridge write path.
// Prior alias 'seaready_checklist_records' was invisible to cross-device
// hydration because UniversalSyncBridge writes to `checklist_records_${vesselId}`.
// One-shot migration in the vessel-load effect merges any legacy alias data
// forward and removes the old key.
const STORAGE_KEY = 'checklist_records';
const MIGRATION_STORAGE_KEY = 'seaready_checklist_records';

export const useChecklistRecords = () => {
  const { getCurrentVessel } = useVessel();
  const vessel = getCurrentVessel();
  const vesselId = vessel?.id;

  // FT-324 cycle 2: storage backed by useStorageState. `immediate: true` bypasses
  // the 50ms batch debounce so writes go through storageRouter → LS+IDB right
  // away — required for the FT-311 storage-first invariant (writes visible in
  // storage synchronously so the hook can survive its own execution-modal
  // unmount) and to prevent the sync bridge from missing pushes on the
  // next-tick teardown of the execution view.
  const {
    value: rawRecords,
    setValue: setRecords
  } = useStorageState(STORAGE_KEY, [], { immediate: true });

  // Defensive normaliser — `useStorageState.value` can theoretically be any
  // JSON shape (e.g. if a rogue hydrate event delivered a non-array). Downstream
  // code expects `records` to always be an array so all `.filter/.map/.slice`
  // calls stay safe. Keeps parity with the pre-cycle-2 API surface.
  const records = Array.isArray(rawRecords) ? rawRecords : [];

  // FT-324 W1 (2026-07-06): one-shot migration from alias key to canonical key.
  // Runs on every mount but is a cheap no-op if the alias key is empty
  // (which becomes the steady state after first-run per device+vessel).
  // Merge strategy: dedupe by id, canonical key wins on collision.
  // Writes via storage.js writeJSON so sr:storage:set fires — useStorageState's
  // same-tab listener picks it up and re-renders with the migrated records.
  useEffect(() => {
    if (!vesselId) return;
    try {
      const oldKey = `${MIGRATION_STORAGE_KEY}_${vesselId}`;
      const oldRaw = readJSON(oldKey, null);
      if (Array.isArray(oldRaw) && oldRaw.length > 0) {
        const newRaw = readJSON(`${STORAGE_KEY}_${vesselId}`, []) || [];
        const byId = new Map();
        oldRaw.forEach((r) => { if (r && r.id) byId.set(r.id, r); });
        (Array.isArray(newRaw) ? newRaw : []).forEach((r) => {
          if (r && r.id) byId.set(r.id, r); // canonical wins on id collision
        });
        const merged = Array.from(byId.values());
        // Write canonical FIRST, only remove alias if write succeeded.
        // Previously we always removed the alias even when writeJSON returned
        // false (e.g. quota-exceeded) — a silent-data-loss path exactly like
        // the class of bug this ticket exists to fix.
        const writeOk = writeJSON(`${STORAGE_KEY}_${vesselId}`, merged);
        if (writeOk) {
          // Use storage.removeItem (strips both LS and IDB via org-scoped path)
          // instead of raw localStorage.removeItem — avoids orphaning IDB alias
          // data and satisfies no-restricted-syntax.
          try {
            removeItem(oldKey);
          } catch (removeErr) {
            // eslint-disable-next-line no-console
            console.warn('[FT-324] Migration cleanup of alias key failed (non-fatal):', removeErr);
          }
          // eslint-disable-next-line no-console
          console.log(
            `[FT-324] Migrated ${oldRaw.length} records from ${oldKey} → ${STORAGE_KEY}_${vesselId} (merged=${merged.length})`
          );
        } else {
          // eslint-disable-next-line no-console
          console.warn(
            `[FT-324] Migration write failed (quota?), keeping alias key intact — will retry on next mount`
          );
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[FT-324] Migration error (non-fatal):', err);
    }
  }, [vesselId]);

  /**
   * Save a new completion record
   * Validates before saving.
   *
   * FT-311 storage-first invariant:
   *   `useStorageState.setValue` performs the localStorage.setItem write
   *   synchronously BEFORE React commits — see useStorageState.js:501-506.
   *   By using the function updater form we also guarantee that rapid
   *   successive saves in the same tick see each other's writes via
   *   valueRef.current, so all three records land instead of only the last.
   */
  const saveCompletionRecord = useCallback((recordData) => {
    const record = createCompletionRecord({
      ...recordData,
      vesselId,
      vesselName: vessel?.name || 'Unknown Vessel'
    });

    const validation = validateCompletionRecord(record);
    if (!validation.valid) {
      return { success: false, errors: validation.errors };
    }

    // Function updater form — reads freshest value from valueRef.current
    // inside useStorageState, safe under rapid synchronous saves.
    setRecords((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];
      return [record, ...prevArr];
    });

    return { success: true, record };
  }, [vesselId, vessel?.name, setRecords]);

  /**
   * Get records by type
   */
  const getRecordsByType = useCallback((checklistType) => {
    return records.filter(r => r.checklistType === checklistType);
  }, [records]);

  /**
   * Get records for a specific checklist
   */
  const getRecordsForChecklist = useCallback((checklistId) => {
    return records.filter(r => r.checklistId === checklistId);
  }, [records]);

  /**
   * Get last completion for a checklist
   */
  const getLastCompletion = useCallback((checklistId) => {
    const checklistRecords = getRecordsForChecklist(checklistId);
    return checklistRecords[0] || null;
  }, [getRecordsForChecklist]);

  /**
   * Get active permits (not expired, not closed)
   */
  const getActivePermits = useCallback(() => {
    const now = new Date();
    return records.filter(r => {
      if (r.checklistType !== CHECKLIST_TYPES.PERMIT_TO_WORK) return false;
      if (!r.expiresAt) return true;
      if (r.closedAt) return false;
      return new Date(r.expiresAt) > now;
    });
  }, [records]);

  /**
   * Close a permit (separate record for audit trail)
   *
   * FT-311 storage-first invariant preserved — same rationale as
   * saveCompletionRecord above. Uses function updater so the mapping over
   * the previous list sees the freshest value even if closePermit is
   * chained after a saveCompletionRecord in the same tick.
   */
  const closePermit = useCallback((permitId, closureData) => {
    const closureRecord = {
      id: `CLOSE-${Date.now()}`,
      type: 'permit_closure',
      permitId,
      closedAt: new Date().toISOString(),
      closedBy: closureData.closedBy,
      closureSignature: closureData.signature,
      workCompleted: closureData.workCompleted,
      areaCleared: closureData.areaCleared,
      notes: closureData.notes || ''
    };

    setRecords((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];
      const mapped = prevArr.map(r => {
        if (r.id === permitId) {
          return { ...r, closedAt: closureRecord.closedAt, closureRecordId: closureRecord.id };
        }
        return r;
      });
      mapped.unshift(closureRecord);
      return mapped;
    });

    return { success: true, closureRecord };
  }, [setRecords]);

  /**
   * Get today's daily check completions
   */
  const getTodaysDailyChecks = useCallback(() => {
    const todayKey = new Date().toISOString().split('T')[0];
    return records.filter(r =>
      r.checklistType === CHECKLIST_TYPES.DAILY_CHECK &&
      r.completedAt.startsWith(todayKey)
    );
  }, [records]);

  /**
   * Check if a daily check category is completed today
   */
  const isDailyCheckCompletedToday = useCallback((categoryId) => {
    const todayKey = new Date().toISOString().split('T')[0];
    return records.some(r =>
      r.checklistType === CHECKLIST_TYPES.DAILY_CHECK &&
      r.checklistId === categoryId &&
      r.completedAt.startsWith(todayKey)
    );
  }, [records]);

  /**
   * Get today's completion for a daily check category
   */
  const getTodayCompletion = useCallback((categoryId) => {
    const todayKey = new Date().toISOString().split('T')[0];
    return records.find(r =>
      r.checklistType === CHECKLIST_TYPES.DAILY_CHECK &&
      r.checklistId === categoryId &&
      r.completedAt.startsWith(todayKey)
    ) || null;
  }, [records]);

  // ========================================
  // CONTEXT LINKING QUERIES
  // ========================================

  /**
   * Get all checklists linked to a specific voyage
   */
  const getChecklistsForVoyage = useCallback((voyageId) => {
    return records.filter(r =>
      r.linkedContext?.type === 'voyage' &&
      r.linkedContext?.id === voyageId
    );
  }, [records]);

  /**
   * Get all checklists linked to a specific equipment item
   */
  const getChecklistsForEquipment = useCallback((equipmentId) => {
    return records.filter(r =>
      r.linkedContext?.type === 'equipment' &&
      r.linkedContext?.id === equipmentId
    );
  }, [records]);

  /**
   * Get all checklists linked to a specific maintenance job
   */
  const getChecklistsForMaintenanceJob = useCallback((jobId) => {
    return records.filter(r =>
      r.linkedContext?.type === 'maintenance_job' &&
      r.linkedContext?.id === jobId
    );
  }, [records]);

  /**
   * Get recent completions (for sidebar display)
   */
  const getRecentCompletions = useCallback((limit = 5) => {
    return records
      .filter(r => r.checklistType && !r.type) // Exclude closure records
      .slice(0, limit);
  }, [records]);

  /**
   * Get completion count for today
   */
  const getTodayCompletionCount = useCallback(() => {
    const todayKey = new Date().toISOString().split('T')[0];
    return records.filter(r =>
      r.completedAt?.startsWith(todayKey) &&
      r.checklistType &&
      !r.type
    ).length;
  }, [records]);

  return {
    records,
    saveCompletionRecord,
    getRecordsByType,
    getRecordsForChecklist,
    getLastCompletion,
    getActivePermits,
    closePermit,
    getTodaysDailyChecks,
    isDailyCheckCompletedToday,
    getTodayCompletion,

    // Context linking
    getChecklistsForVoyage,
    getChecklistsForEquipment,
    getChecklistsForMaintenanceJob,

    // UI helpers
    getRecentCompletions,
    getTodayCompletionCount
  };
};

export default useChecklistRecords;
